import React, { useCallback, useEffect, useState } from "react";
import axios from "../axiosConfig";

// Scripts used/remaining in the teacher's plan, refreshed after each marked script.
export function useMarkingAllowance(apiUrl) {
  const [allowance, setAllowance] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/marking_usage.php`);
      setAllowance(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, [apiUrl]);
  useEffect(() => {
    refresh();
    // Returning from a top-up payment: the webhook may land a few seconds after the redirect.
    if (!new URLSearchParams(window.location.search).has("topup")) return undefined;
    const timers = [3000, 8000, 15000].map((delay) => setTimeout(refresh, delay));
    return () => timers.forEach(clearTimeout);
  }, [refresh]);
  return { allowance, setAllowance, refresh };
}

const formatRand = (cents) => `R${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

export async function startTopupCheckout(apiUrl) {
  const response = await axios.post(`${apiUrl}/buy_marking_topup.php`);
  if (response.data?.redirectUrl) window.location.href = response.data.redirectUrl;
  else throw new Error(response.data?.error || "Could not start the top-up payment.");
}

export default function MarkingAllowance({ apiUrl, allowance }) {
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState("");
  if (!allowance || allowance.unlimited) return null;

  const { included, used, topup_remaining: topupLeft, remaining, period_end: periodEnd, plan_label: planLabel, topup } = allowance;
  const percent = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 100;
  const low = remaining <= Math.max(5, included * 0.1);
  const buy = async () => {
    setBuying(true);
    setError("");
    try {
      await startTopupCheckout(apiUrl);
    } catch (buyError) {
      setError(buyError.response?.data?.error || buyError.message);
      setBuying(false);
    }
  };

  return (
    <section className={`marking-allowance ${low ? "is-low" : ""}`} aria-label="Marking allowance">
      <div className="marking-allowance-text">
        <strong>
          {planLabel ? `${used} of ${included} scripts used` : "No active plan includes AI marking"}
        </strong>
        <span>
          {planLabel && `${planLabel} plan`}
          {topupLeft > 0 && ` · ${topupLeft} top-up script${topupLeft === 1 ? "" : "s"} left`}
          {periodEnd && ` · resets ${new Date(periodEnd.replace(" ", "T") + "Z").toLocaleDateString()}`}
        </span>
        {planLabel && (
          <div className="marking-allowance-meter" role="progressbar" aria-valuemin={0} aria-valuemax={included} aria-valuenow={used}>
            <div style={{ width: `${percent}%` }} />
          </div>
        )}
      </div>
      {topup && (
        <button type="button" className="marking-allowance-buy" onClick={buy} disabled={buying}>
          {buying ? "Opening payment…" : `Buy ${topup.scripts} more scripts · ${formatRand(topup.price_cents)}`}
        </button>
      )}
      {error && <p className="marking-allowance-error">{error}</p>}
    </section>
  );
}
